/**
 * usePreloadGameEndScreenAsset — Preloads assets for the game end sequence.
 *
 * Enqueued at low priority since game end is not imminent when this first runs.
 * The caller can promote via the returned batchUrls when game over is determined.
 */

import { useEffect, useRef } from "react";
import { useGameEndData } from "@/state/cacheStoreHooks";
import { preloadManager } from "@/libs/assetPreloading/PreloadManager";
import { StrapiAsset } from "@/types/api";

export function usePreloadGameEndScreenAsset(roomImage: StrapiAsset): void {
    const gameEndData = useGameEndData();
    const itemImageA = gameEndData?.polaroidItemA;
    const itemImageB = gameEndData?.polaroidItemB;
    const enqueuedRef = useRef(false);

    useEffect(() => {
        try {
            if (enqueuedRef.current) return;
            const iconImages: StrapiAsset[] = [];

            if (itemImageA) iconImages.push(itemImageA);
            if (itemImageB) iconImages.push(itemImageB);

            if (!roomImage || iconImages.length === 0) return;

            enqueuedRef.current = true;
            preloadManager.enqueueImages([roomImage], "lg", "low");
            preloadManager.enqueueImages(iconImages, "md", "low");
        } catch (err) {
            // Preload failures must never break gameplay — log and move on
            console.warn("Failed to enqueue game end screen assets:", err);
        }
    }, [roomImage, itemImageA, itemImageB]);
}